import SeoHead from "../../components/Seo/SeoHead";
import WhatsAppMockup from "../../components/Marketing/WhatsAppMockup";
import FaqAccordion from "../../components/Marketing/FaqAccordion";
import { Link } from "react-router-dom";
import {
  TbScissors,
  TbPhoneOff,
  TbBell,
  TbMoon,
  TbCircleCheck,
} from "react-icons/tb";
import styles from "./Barbearias.module.css";
import {
  BUSINESS_SEGMENTS,
  CTA_PAGES,
  CTA_PLACEMENTS,
  DESTINATIONS,
  trackTrialCtaClick,
} from "../../analytics";
import {
  TRIAL_PRODUCTS,
  buildTrialSignupPath,
} from "../../constants/trialProduct";

const BARBEARIAS_FAQ = [
  {
    question: "O cliente pode escolher o barbeiro preferido?",
    answer:
      "Sim. O bot pergunta qual barbeiro o cliente quer e só mostra os horários disponíveis daquele profissional.",
  },
  {
    question: "Funciona para agendamentos de última hora?",
    answer:
      "Sim. O cliente agenda pelo WhatsApp a qualquer hora, inclusive 5 minutos antes — se o horário estiver livre.",
  },
  {
    question: "E se o barbeiro cancelar o horário?",
    answer:
      "O cancelamento você faz pelo painel, e o horário liberado vai para quem estava na lista de espera daquele horário — essa pessoa o bot avisa sozinho. O aviso ao cliente que teve o horário cancelado ainda é você quem manda.",
  },
  {
    question: "Posso ter mais de um barbeiro na conta?",
    answer:
      "Sim, cada barbeiro tem a sua própria agenda e o cliente escolhe com quem quer. Quantos cabem depende do plano: o Básico inclui 2 profissionais e o Profissional, 10.",
  },
];

const BARBEARIAS_MOCKUP = [
  { type: "out", text: "Oi! Quero marcar corte + barba 💈" },
  {
    type: "in",
    text: "Qual barbeiro você prefere?",
    buttons: ["João", "Pedro", "Tanto faz"],
  },
  { type: "out", text: "João" },
  {
    type: "in",
    text: "João tem horário amanhã 10h ou sexta 14h. Qual fica melhor?",
  },
  { type: "out", text: "Amanhã 10h" },
  {
    type: "in",
    text: "✅ Agendado! Corte + barba com João amanhã às 10h. Vou te lembrar às 8h.",
  },
];

const JSON_LD = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Chatbot de WhatsApp para Barbearia",
  provider: { "@type": "Organization", name: "Nexus Chatbot" },
  areaServed: "BR",
  serviceType: "Chatbot de WhatsApp para Barbearia",
};

const FAQ_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: BARBEARIAS_FAQ.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: { "@type": "Answer", text: f.answer },
  })),
};

const BENEFITS = [
  {
    Icon: TbPhoneOff,
    title: "Sem interrupções",
    text: "Pare de parar o corte pra atender o telefone.",
  },
  {
    Icon: TbScissors,
    title: "Barbeiro preferido",
    text: "Cliente escolhe com quem quer e quando quer.",
  },
  {
    Icon: TbBell,
    title: "Zero faltas",
    text: "Lembrete automático reduz no-show drasticamente.",
  },
  {
    Icon: TbMoon,
    title: "Agenda 24/7",
    text: "Recebe agendamentos mesmo de madrugada.",
  },
];

const RECURSOS = [
  "Escolha de barbeiro pelo WhatsApp",
  "Lista de espera automática",
  "Combo barba + sobrancelha no cardápio",
  "Lembrete 24h antes do horário",
  'Histórico do cliente — "Da última vez foi degradê na régua, quer o mesmo?" — fidelização automática.',
];

/** Contexto fixo desta página para o funil de teste grátis (GA4). */
const CTA_CTX = {
  cta_page: CTA_PAGES.BARBEARIAS,
  business_segment: BUSINESS_SEGMENTS.BARBEARIAS,
  destination: DESTINATIONS.LOGIN_SIGNUP,
  // Página de segmento vende AGENDA (o cliente marca hora): o teste que ela
  // oferece é o de Agendamento, e o `?produto=` leva isso ao signup.
  product: TRIAL_PRODUCTS.AGENDAMENTO,
};

/** Cadastro já carregando o produto — o backend escolhe o plano de trial. */
const SIGNUP_PATH = buildTrialSignupPath(TRIAL_PRODUCTS.AGENDAMENTO);

const Barbearias = () => (
  <>
    <SeoHead
      title="Chatbot WhatsApp para Barbearia que Agenda 24/7 | Nexus Chatbot"
      description="Seu cliente agenda pelo WhatsApp a qualquer hora, escolhe o barbeiro e recebe lembrete automático. Teste grátis 14 dias."
      canonical="https://nexustech.tec.br/barbearias"
      jsonLd={[JSON_LD, FAQ_LD]}
    />

    {/* Hero */}
    <section className={styles.hero}>
      <div className={styles.heroInner}>
        <div className={styles.heroCol}>
          <h1 className={styles.heroTitle}>
            <TbScissors
              className={styles.titleIcon}
              size={36}
              aria-hidden="true"
            />
            Chatbot de WhatsApp para Barbearia que Agenda 24/7
          </h1>
          <p className={styles.heroText}>
            Seu cliente escolhe o barbeiro, marca o horário e recebe o lembrete
            — tudo pelo WhatsApp, sem você precisar parar o corte para atender.
          </p>
          <Link
            to={SIGNUP_PATH}
            className={styles.ctaPrimary}
            onClick={() =>
              trackTrialCtaClick({
                ...CTA_CTX,
                cta_placement: CTA_PLACEMENTS.HERO,
              })
            }
          >
            Testar grátis 14 dias
          </Link>
          <p className={styles.heroNote}>Sem cartão. Cancele quando quiser.</p>
        </div>
        <div className={styles.heroMockup}>
          <WhatsAppMockup messages={BARBEARIAS_MOCKUP} looping />
        </div>
      </div>
    </section>

    {/* Por que barbearias amam a Nexus */}
    <section className={styles.section}>
      <div className={styles.sectionInner}>
        <h2 className={styles.sectionTitle}>Por que barbearias amam a Nexus</h2>

        <div className={styles.benefitsGrid}>
          {BENEFITS.map((item, i) => (
            <div key={i} className={styles.benefitCard}>
              <item.Icon
                className={styles.benefitIcon}
                size={36}
                aria-hidden="true"
              />
              <h3 className={styles.benefitTitle}>{item.title}</h3>
              <p className={styles.benefitText}>{item.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* CTA intermediário */}
    <section className={styles.ctaMid}>
      <div className={styles.ctaMidInner}>
        <p className={styles.ctaMidText}>
          Configuração em 15 minutos. Teste por 14 dias sem pagar nada.
        </p>
        <Link
          to={SIGNUP_PATH}
          className={styles.ctaPrimary}
          onClick={() =>
            trackTrialCtaClick({
              ...CTA_CTX,
              cta_placement: CTA_PLACEMENTS.MIDDLE,
            })
          }
        >
          Quero testar agora
        </Link>
      </div>
    </section>

    {/* Recursos pensados pra barbearia */}
    <section className={styles.section}>
      <div className={styles.sectionInner}>
        <h2 className={styles.sectionTitle}>Recursos pensados pra barbearia</h2>

        <div className={styles.recursosGrid}>
          {RECURSOS.map((r, i) => (
            <div key={i} className={styles.recursoCard}>
              <p className={styles.recursoText}>
                <TbCircleCheck
                  className={styles.recursoIcon}
                  size={18}
                  aria-hidden="true"
                />
                {r}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* FAQ */}
    <section className={styles.faq}>
      <div className={styles.faqInner}>
        <h2 className={styles.faqTitle}>Perguntas frequentes</h2>
        <FaqAccordion items={BARBEARIAS_FAQ} />
      </div>
    </section>

    {/* CTA final */}
    <section className={styles.ctaFinal}>
      <div className={styles.ctaFinalInner}>
        <h2 className={styles.ctaFinalTitle}>
          Pronto pra parar de atender no telefone?
        </h2>
        <p className={styles.ctaFinalText}>
          Comece agora e configure em menos de 15 minutos.
        </p>
        <Link
          to={SIGNUP_PATH}
          className={styles.ctaInverse}
          onClick={() =>
            trackTrialCtaClick({
              ...CTA_CTX,
              cta_placement: CTA_PLACEMENTS.FINAL,
            })
          }
        >
          Testar grátis 14 dias
          <TbScissors size={18} aria-hidden="true" />
        </Link>
      </div>
    </section>
  </>
);

export default Barbearias;
